import React, { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

const CookieConsent = ({ darkMode, lang }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const translations = {
    lv: {
      title: 'Mēs izmantojam sīkdatnes',
      description: 'Šī vietne izmanto sīkdatnes, lai uzlabotu lietotāja pieredzi un nodrošinātu vietnes funkcionalitāti.',
      necessary: 'Nepieciešamās sīkdatnes',
      necessaryDesc: 'Šīs sīkdatnes ir būtiskas vietnes darbībai. Tās nodrošina pamata funkcijas, piemēram, iestatījumu saglabāšanu.',
      analytics: 'Analītiskās sīkdatnes',
      analyticsDesc: 'Palīdz mums saprast, kā apmeklētāji izmanto vietni, lai varētu to uzlabot.',
      learnMore: 'Uzzināt vairāk',
      hideDetails: 'Paslēpt detaļas',
      acceptAll: 'Piekrist visam',
      acceptNecessary: 'Tikai nepieciešamās',
      privacyPolicy: 'Privātuma politika'
    },
    en: {
      title: 'We use cookies',
      description: 'This website uses cookies to improve user experience and ensure website functionality.',
      necessary: 'Necessary cookies',
      necessaryDesc: 'These cookies are essential for the website to function. They enable basic features such as saving preferences.',
      analytics: 'Analytics cookies',
      analyticsDesc: 'Help us understand how visitors use the site so we can improve it.',
      learnMore: 'Learn more',
      hideDetails: 'Hide details',
      acceptAll: 'Accept all',
      acceptNecessary: 'Necessary only',
      privacyPolicy: 'Privacy policy'
    }
  };

  const t = translations[lang] || translations.lv;

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40">
      <div 
        className={`w-full max-w-lg rounded-xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-slate-800' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`p-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              darkMode ? 'bg-emerald-900/50' : 'bg-emerald-100'
            }`}>
              <Cookie className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {t.title}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`p-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          <p className="text-sm leading-relaxed mb-4">
            {t.description}
          </p>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={`flex items-center gap-1 text-sm font-medium mb-3 transition-colors ${
              darkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
            }`}
          >
            {showDetails ? t.hideDetails : t.learnMore}
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Cookie details */}
          {showDetails && (
            <div className={`space-y-3 mb-4 p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
              {/* Necessary cookies */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {t.necessary}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    darkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {lang === 'lv' ? 'Vienmēr aktīvas' : 'Always active'}
                  </span>
                </div>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {t.necessaryDesc}
                </p>
              </div>

              {/* Analytics cookies */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {t.analytics}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    darkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {lang === 'lv' ? 'Izvēles' : 'Optional'}
                  </span>
                </div>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {t.analyticsDesc}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`p-4 border-t ${darkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleAcceptNecessary}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                darkMode 
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              {t.acceptNecessary}
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
            >
              {t.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
